/**
 * settings.tsx — one settings look for both apps (§7.17): the layout (header
 * with search, nav, content, optional preview), the nav item, the setting row
 * and the theme grid. The terminal keeps its registry / stores / snapshots and
 * the site its own state — both only render through these.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { THEMES } from '@quantum/tokens/themes';
import { Button, IconButton, SearchField } from './components';
import { tr } from './i18n';

export function SettingsLayout({ title, subtitle, query, onQuery, changed, onDone, doneLabel, nav, children, preview, headerExtra, flyout, fit, widths, onWidths, ratio, countLabel }: {
  title: string; subtitle?: string;
  query: string; onQuery: (q: string) => void;
  /** "N changed from default" */
  changed?: number;
  onDone?: () => void;
  doneLabel?: string;
  nav: ReactNode;
  children: ReactNode;
  preview?: ReactNode;
  headerExtra?: ReactNode;
  /** A side panel laid over the preview column (rarely-used / heavy options). */
  flyout?: ReactNode;
  /** no scrolling in the body: the caller keeps each view within one column */
  fit?: boolean;
  /** Column widths in px (nav, preview). With `onWidths` the gaps between the blocks become drag handles (layout-edit mode). */
  widths?: { nav: number; preview: number } | null;
  onWidths?: (w: { nav: number; preview: number }) => void;
  /** fixed 15 : 40 : 60 columns (owner 08.10.2026) instead of the default grid */
  ratio?: boolean;
  /** replaces the "N changed from default" text */
  countLabel?: string;
}) {
  const searchWrap = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const drag = (e: React.PointerEvent, which: 'nav' | 'preview') => {
    if (!widths || !onWidths) return;
    e.preventDefault();
    const x0 = e.clientX, w0 = widths, total = root.current?.clientWidth ?? 1200;
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - x0;
      const n = which === 'nav' ? { ...w0, nav: w0.nav + dx } : { ...w0, preview: w0.preview - dx };
      onWidths({ nav: Math.max(140, Math.min(320, n.nav)), preview: Math.max(240, Math.min(total * 0.7, n.preview)) });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  // Ctrl+F focuses the search, like the hint on the field says.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        searchWrap.current?.querySelector('input')?.focus();
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);
  return (
    <div
      ref={root} className="qt-settings" data-preview={preview ? 'true' : 'false'} data-fit={fit || undefined} data-ratio={ratio || undefined}
      style={widths ? { gridTemplateColumns: preview ? `${widths.nav}px minmax(0,1fr) ${widths.preview}px` : `${widths.nav}px minmax(0,1fr)` } : undefined}
    >
      <header className="qt-settings__hd">
        <div className="qt-settings__title">{title}{subtitle && <span> · {subtitle}</span>}</div>
        <div ref={searchWrap} style={{ flex: 1, maxWidth: 360 }}>
          <SearchField value={query} onChange={onQuery} placeholder={tr('Search settings…')} hotkey="Ctrl F" />
        </div>
        {headerExtra}
        <span style={{ flex: 1 }} />
        {changed != null && <span className="qt-settings__count" data-n={changed || undefined}>{countLabel ? tr(countLabel, { n: changed }) : tr('{n} changed from default', { n: changed })}</span>}
        {onDone && <Button size="sm" variant={doneLabel ? 'primary' : 'secondary'} onClick={onDone}>{doneLabel ?? tr('Done')}</Button>}
      </header>
      <nav className="qt-settings__nav" aria-label={title}>{nav}</nav>
      <div className="qt-settings__body">{children}</div>
      {preview && <aside className="qt-settings__preview">{preview}{flyout}</aside>}
      {widths && onWidths && (
        <>
          <div className="qt-settings__split" style={{ left: `calc(var(--sp3) + ${widths.nav}px)` }} onPointerDown={(e) => drag(e, 'nav')} role="separator" aria-orientation="vertical" />
          {preview && <div className="qt-settings__split" style={{ right: `calc(var(--sp3) + ${widths.preview}px)` }} onPointerDown={(e) => drag(e, 'preview')} role="separator" aria-orientation="vertical" />}
        </>
      )}
    </div>
  );
}

export function SettingsNavItem({ icon, label, active, changed, onClick }: {
  icon?: ReactNode; label: string; active?: boolean; changed?: boolean; onClick: () => void;
}) {
  return (
    <button type="button" className="qt-settings__navitem" aria-current={active || undefined} onClick={onClick}>
      {icon}<span>{label}</span>{changed && <span className="qt-settings__dot" aria-label={tr('changed from default')} />}
    </button>
  );
}

export const SettingsGroupTitle = ({ children }: { children: ReactNode }) => <div className="qt-settings__group">{children}</div>;

/** One row: label + help on the left, the control slot, a dot when changed and
 *  a ↺ that only exists while it is. `favourite` and the hold handlers are
 *  optional extras the terminal uses. */
export function SettingRowView({ label, help, changed, onReset, children, favourite, onHoldStart, onHoldEnd, onSpot, onSpotEnd, wide, compact }: {
  label: ReactNode; help?: ReactNode; changed?: boolean; onReset?: () => void; children?: ReactNode;
  favourite?: ReactNode;
  onHoldStart?: () => void; onHoldEnd?: () => void;
  /** pointer enters / focus enters the row (spotlight), and leaves */
  onSpot?: () => void; onSpotEnd?: () => void;
  /** the control takes its own line under the text (custom bodies) */
  wide?: boolean;
  /** one line: the help text moves into a tooltip */
  compact?: boolean;
}) {
  return (
    <div className="qt-setrow" data-wide={wide || undefined} data-compact={compact || undefined}
      onPointerEnter={onSpot} onPointerLeave={onSpotEnd} onFocus={onSpot} onBlur={onSpotEnd}>
      <div className="qt-setrow__text" data-tip={compact && typeof help === 'string' ? help : undefined}
        onPointerDown={(e) => { if (e.button === 0) onHoldStart?.(); }}
        onPointerUp={onHoldEnd} onPointerCancel={onHoldEnd}
        title={onHoldStart ? tr('Hold to compare with the value before you started') : undefined}>
        <div className="qt-setrow__label">{favourite}{label}{changed && <span className="qt-setrow__changed" aria-label={tr('changed from default')} />}</div>
        {help && !compact && <div className="qt-setrow__help">{help}</div>}
      </div>
      <div className="qt-setrow__ctl">
        {children}
        {changed && onReset && <IconButton label={tr('Reset to default')} onClick={onReset}>↺</IconButton>}
      </div>
    </div>
  );
}

/** The 9-theme picker, shared by the terminal's Appearance section and the site. */
export function ThemeGrid({ active, onPick }: { active: string; onPick: (id: string) => void }) {
  return (
    <div className="qt-themegrid">
      {THEMES.map((t) => (
        <button key={t.id} type="button" aria-pressed={active === t.id} title={tr(t.hint)} onClick={() => onPick(t.id)} className="qt-themegrid__item">
          <span className="qt-themegrid__sw" style={{ borderColor: t.c.line2 }}>
            <span style={{ flex: 2, background: t.c.void }} />
            <span style={{ flex: 2, background: t.c.s2 }} />
            <span style={{ flex: 1, background: t.c.acc }} />
            <span style={{ flex: 1, background: t.c.bull }} />
            <span style={{ flex: 1, background: t.c.bear }} />
          </span>
          <span className="qt-themegrid__name">{tr(t.label)}</span>
        </button>
      ))}
    </div>
  );
}
